import { MSALObj, tokenRequest } from "app/providers/auth/authConfig";
import axios, { AxiosRequestConfig } from "axios";
import { stringify } from "query-string";
import { DataProvider, fetchUtils } from "react-admin";
import { ResourceMapping } from "shared/enums/resourceMappings";
import { buildFormData } from "./helpers";

export const axiosRequest = async ({ method, url, data = {}, params = {}, headers = {} }: AxiosRequestConfig) => {
  const account = MSALObj.getActiveAccount();
  const authResult = await MSALObj.acquireTokenSilent({
    account: account || undefined,
    ...tokenRequest,
  });
  const defaultHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${authResult.accessToken}`,
  };
  const combinedHeaders = { ...defaultHeaders, ...headers };

  return axios({
    method,
    url,
    data,
    params,
    headers: combinedHeaders,
  })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.error("Axios request failed:", error);
      throw error;
    });
};

export const httpClient = async (url: string, options: any = {}) => {
  const account = MSALObj.getActiveAccount();
  let authResult;

  try {
    // Attempt to silently acquire a token
    authResult = await MSALObj.acquireTokenSilent({
      account: account || undefined,
      ...tokenRequest,
    });
  } catch (error) {
    console.error("Error acquiring token silently", error);
    // If acquireTokenSilent fails, fallback to a popup or redirect
    try {
      authResult = await MSALObj.acquireTokenPopup({
        ...tokenRequest,
      });
    } catch (popupError) {
      console.error("Error acquiring token with popup", popupError);
      throw popupError;
    }
  }

  if (!options.headers) {
    options.headers = new Headers({
      Accept: "application/json",
    });
  }
  options.headers.append("Authorization", `Bearer ${authResult.accessToken}`);
  return fetchUtils.fetchJson(url, options);
};

export default (apiUrl: string): DataProvider => {
  return {
    getList: async (resource, params) => {
      resource = ResourceMapping[resource as any] as unknown as string;
      const { page, perPage } = params.pagination || { page: 1, perPage: 10 };
      const { field, order } = params.sort || { field: undefined, order: undefined };
      const query = {
        page: page,
        size: perPage,
        ...params.filter,
      };
      if (field) {
        query.order_by = (order == "ASC" ? "+" : "-") + field;
      }
      if (query.q) {
        query.search = params.filter.q;
        delete query.q;
      }
      if (params.meta && params.meta.urlParams) {
        resource = `${resource}${params.meta.urlParams}`;
      }
      const url = `${apiUrl}/${resource}?${stringify(query, {
        arrayFormat: "separator",
        arrayFormatSeparator: ",",
      })}`;
      const { json } = await httpClient(url);
      return {
        data: json.items,
        total: json.total,
        page: json.page,
        size: json.size,
      };
    },

    getOne: async (resource, params, url = null) => {
      resource = ResourceMapping[resource as any] as unknown as string;
      let metaUrlQuery = "";
      if (params.meta && params.meta.urlParams) {
        metaUrlQuery = params.meta.urlParams;
      }
      const { json } = url
        ? await httpClient(url)
        : await httpClient(`${apiUrl}/${resource.replace(/\/$/, "")}/${params.id}${metaUrlQuery}`);
      return {
        data: json,
      };
    },

    getMany: async (resource, params) => {
      resource = ResourceMapping[resource as any] as unknown as string;
      const query = {
        id__in: params.ids,
      };
      const url = `${apiUrl}/${resource}?${stringify(query, {
        arrayFormat: "separator",
        arrayFormatSeparator: ",",
      })}`;
      const { json } = await httpClient(url);
      return { data: json.items };
    },

    getManyReference: async (resource, params) => {
      resource = ResourceMapping[resource as any] as unknown as string;
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const query = {
        sort: JSON.stringify([field, order]),
        range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
        filter: JSON.stringify({
          ...params.filter,
          [params.target]: params.id,
        }),
      };
      const url = `${apiUrl}/${resource}?${stringify(query)}`;

      const { headers, json } = await httpClient(url);
      return {
        data: json,
        total: parseInt(headers.get("content-range")?.split("/").pop() || "0", 10),
      };
    },

    create: async (resource, params: any) => {
      let mappedResource = ResourceMapping[resource as any] as unknown as string;

      // Handle optional URL params (e.g. "sites/upload")
      if (params.meta?.urlParams) {
        mappedResource = `${mappedResource}${params.meta.urlParams}`;
      }

      const url = `${apiUrl}/${mappedResource}`;

      // ---- MULTIPART MODE ----
      if (params.meta?.multipart) {
        const formData = buildFormData(params.data, {
          fieldNameMapping: params.meta?.fieldNameMapping,
        });

        // httpClient will add Authorization; DO NOT set Content-Type here
        const { json } = await httpClient(url, {
          method: "POST",
          body: formData,
        });

        return { data: json };
      }

      // ---- DEFAULT JSON MODE ----
      const { json } = await httpClient(url, {
        method: "POST",
        body: JSON.stringify(params.data),
      });

      return { data: { ...json } };
    },

    update: async (resource, params: any) => {
      const mappedResource = ResourceMapping[resource as any] as unknown as string;
      let method: "PUT" | "PATCH" = "PUT";
      if (params.meta?.method) {
        method = params.meta.method;
      }

      const url = `${apiUrl}/${mappedResource.replace(/\/$/, "")}/${params.id}`;

      // ---- MULTIPART MODE ----
      if (params.meta?.multipart) {
        const formData = buildFormData(
          { ...params.data, id: params.id },
          {
            fieldNameMapping: params.meta?.fieldNameMapping,
          },
        );

        const { json } = await httpClient(url, {
          method,
          body: formData,
        });

        if (!("id" in json) && params.id) {
          json.id = params.id;
        }
        return { data: json };
      }

      // ---- DEFAULT JSON MODE ----
      const { json } = await httpClient(url, {
        method,
        body: JSON.stringify(params.data),
      });

      if (!("id" in json) && params.id) {
        json.id = params.id;
      }
      return { data: json };
    },

    updateMany: async (resource, params) => {
      resource = ResourceMapping[resource as any] as unknown as string;
      const query = {
        filter: JSON.stringify({ id: params.ids }),
      };
      const { json } = await httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
        method: "PUT",
        body: JSON.stringify(params.data),
      });
      return { data: json };
    },

    delete: async (resource, params) => {
      resource = ResourceMapping[resource as any] as unknown as string;
      return await httpClient(`${apiUrl}/${resource.replace(/\/$/, "")}/${params.id}`, {
        method: "DELETE",
      }).then(({ json }) => ({ data: json }));
    },

    deleteMany: async (resource, params) => {
      resource = ResourceMapping[resource as any] as unknown as string;
      const query = {
        ids: JSON.stringify(params.ids),
        soft: true,
      };
      const { json } = await httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
        method: "DELETE",
        body: JSON.stringify(params.ids),
      });
      return { data: json.deleted_ids };
    },
  };
};
